import React, { useState } from 'react';
import { User, AvatarVisibility } from '@/types';
import styles from './W_PrivacyTab.module.css';
import A_RadioItem from '@/components/A_Atoms/A_RadioItem/A_RadioItem';
import A_CheckboxItem from '@/components/A_Atoms/A_CheckboxItem/A_CheckboxItem';
import { useLanguage } from '@/contexts/LanguageContext';
import { getTranslation } from '@/constants/translations';
import A_Button from '@/components/A_Atoms/A_Button/A_Button';
import S_InviteCoauthorsPopup from '@/components/S_Super-Organisms/S_InviteCoauthorsPopup/S_InviteCoauthorsPopup';
import A_UserAvatar from '@/components/A_Atoms/A_UserAvatar/A_UserAvatar';
import A_CircleButton from '@/components/A_Atoms/A_CircleButton/A_CircleButton';
import { Q_Icon } from '@/components/Q_Quarks/Q_Icon/Q_Icon';
import cn from 'classnames';

interface W_PrivacyTabProps {
  user: User;
}

export const W_PrivacyTab: React.FC<W_PrivacyTabProps> = ({ user }) => {
  const [selectedOption, setSelectedOption] = useState(
    user.preferences?.avatarVisibility || 'everyone'
  );
  const [emailDuplicatesEnabled, setEmailDuplicatesEnabled] = useState(
    Boolean(user.preferences?.emailNotificationsEnabled)
  );
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);
  const [isInvitePopupOpen, setIsInvitePopupOpen] = useState(false);
  const { language } = useLanguage();

  const handleOptionChange = async (value: string) => {
    let apiValue: AvatarVisibility = 'everyone';
    if (value === 'coauthors') {
      apiValue = 'coauthors';
    } else if (value === 'selected') {
      apiValue = 'selected';
    } else if (value === 'everyone') {
      apiValue = 'everyone';
    }

    setSelectedOption(apiValue);
    try {
      const { profileApi: profileApiService } = await import(
        '@/services/profileApi'
      );
      await profileApiService.updateProfile({
        preferences: {
          avatarVisibility: apiValue,
        },
      });
    } catch {
      // ошибка обновления настроек приватности
    }
  };

  const handleEmailDuplicatesChange = async (checked: boolean) => {
    setEmailDuplicatesEnabled(checked);
    try {
      const { profileApi: profileApiService } = await import(
        '@/services/profileApi'
      );
      await profileApiService.updateProfile({
        preferences: {
          emailNotificationsEnabled: checked,
        },
      });
    } catch {
      setEmailDuplicatesEnabled(!checked);
    }
  };

  const handleUserSelect = (selectedUser: User) => {
    if (!selectedUsers.some(u => u.id === selectedUser.id)) {
      setSelectedUsers(prev => [...prev, selectedUser]);
      setIsInvitePopupOpen(false);
    }
  };

  const handleRemoveUser = (userId: string) => {
    setSelectedUsers(prev => prev.filter(u => u.id !== userId));
  };

  const getUserDisplayName = (user: User) => {
    const firstName = user.personal.firstName;
    const lastName = user.personal.lastName;
    if (firstName || lastName) {
      return `${firstName ?? ''} ${lastName ?? ''}`.trim();
    }
    return user.handle || user.id;
  };

  return (
    <div className={styles.wrapper}>
      <h4 className={styles.title}>
        {getTranslation(language, 'profilePrivacy.title')}
      </h4>
      <div className={styles.radio_items_wrapper}>
        <A_RadioItem
          label={getTranslation(language, 'profilePrivacy.all')}
          checked={selectedOption === 'everyone'}
          onChange={() => handleOptionChange('everyone')}
          name='privacy-option'
          value='everyone'
        />
        <A_RadioItem
          label={getTranslation(language, 'profilePrivacy.coauthors')}
          checked={selectedOption === 'coauthors'}
          onChange={() => handleOptionChange('coauthors')}
          name='privacy-option'
          value='coauthors'
        />
        <A_RadioItem
          label={getTranslation(language, 'profilePrivacy.selected')}
          checked={selectedOption === 'selected'}
          onChange={() => handleOptionChange('selected')}
          name='privacy-option'
          value='selected'
        />
      </div>
      <div className={styles.email_duplicates_wrapper}>
        <A_CheckboxItem
          checked={emailDuplicatesEnabled}
          onChange={handleEmailDuplicatesChange}
        >
          {getTranslation(language, 'profilePrivacy.emailDuplicatesEnabled')}
        </A_CheckboxItem>
      </div>
      {selectedOption === 'selected' && (
        <div className={styles.selected_users_wrapper}>
          {selectedUsers.length > 0 ? (
            <div className={styles.users_list}>
              {selectedUsers.map(selectedUser => (
                <div key={selectedUser.id} className={styles.user_item}>
                  <div className={styles.user_info}>
                    {selectedUser.avatar ? (
                      <img
                        src={selectedUser.avatar}
                        alt={getUserDisplayName(selectedUser)}
                        className={styles.user_avatar}
                      />
                    ) : (
                      <A_UserAvatar width={32} height={32} />
                    )}
                    <span className={cn(styles.user_name, 'text-p-7')}>
                      {getUserDisplayName(selectedUser)}
                    </span>
                  </div>
                  <A_CircleButton
                    variant='secondary'
                    icon={
                      <Q_Icon.CrossIcon width={16} height={16} fill='none' />
                    }
                    onClick={() => handleRemoveUser(selectedUser.id)}
                    size='small'
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.empty_state}>
              <span className={cn(styles.empty_text, 'text-p-2')}>
                {getTranslation(language, 'profilePrivacy.noSelectedUsers')}
              </span>
            </div>
          )}
          <A_Button
            variant='tertiary'
            size='small'
            fontSize='text-p-4'
            onClick={() => setIsInvitePopupOpen(true)}
          >
            {getTranslation(language, 'profilePrivacy.invite')}
          </A_Button>
        </div>
      )}
      {isInvitePopupOpen && (
        <S_InviteCoauthorsPopup
          setIsInviteCoauthorsPopupOpen={setIsInvitePopupOpen}
          onUserSelect={handleUserSelect}
        />
      )}
    </div>
  );
};
